import { mapState, mapActions } from 'vuex';
import bus from 'lib/bus';
import {
  handleize,
  swatchOptionMarkup,
  swatchDescription,
  categorySwatchOptionMarkup,
  getVariantSize,
  removeSizeFromTitle,
  convertGQLToProductJson,
} from 'utils/general';
import productSelector from 'mixins/product-selector';
import * as queries from '../../assets/js/lib/graphql/queries';
import storefront from '../../assets/js/lib/storefront';
import {
  getSwatchChangeProductData,
  trackItemView,
} from '../../assets/js/lib/tracking';
import datalayer from '../../assets/js/lib/datalayer';

export default {
  mixins: [productSelector],
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'radio-group': () => import('../radio-group/radio-group.vue'),
    'product-shade-action-buttons': () => import('../product-shade-action-buttons/product-shade-action-buttons.vue')
  },
  props: [
    'decode',
    'hideShadeRangeFilter',
    'initial',
    'isBundle',
    'isSimplifiedTemplate',
    'collectionHandle',
    'productHandlesData',
    'isProductFeature',
  ],
  data() {
    const handlesArray = this.productHandlesData?.split('|') || [];
    return {
      currentVariant: '',
      shades: window.__GLOBAL__.shades.values,
      variantsParsedFull: [],
      activeShade: {
        label: '',
        value: false,
      },
      productHandles: handlesArray,
      localActiveCategory: null,
      categories: [
        {
          label: categorySwatchOptionMarkup('all', 'All Shades'),
          value: 'all',
        },
        {
          label: categorySwatchOptionMarkup('light', 'Light'),
          value: 'light',
        },
        {
          label: categorySwatchOptionMarkup('light-medium', 'Light-Medium'),
          value: 'light-medium',
        },
        {
          label: categorySwatchOptionMarkup('medium', 'Medium'),
          value: 'medium',
        },
        {
          label: categorySwatchOptionMarkup('medium-deep', 'Medium-Deep'),
          value: 'medium-deep',
        },
        {
          label: categorySwatchOptionMarkup('deep', 'Deep'),
          value: 'deep',
        },
        {
          label: categorySwatchOptionMarkup('new', 'New'),
          value: 'new',
        },
        {
          label: categorySwatchOptionMarkup(
            'limited-edition',
            'Limited Edition',
          ),
          value: 'limited-edition',
        },
        {
          label: categorySwatchOptionMarkup('shimmer', 'Shimmer'),
          value: 'shimmer',
        },
        {
          label: categorySwatchOptionMarkup('matte', 'Matte'),
          value: 'matte',
        },
      ],
    };
  },
  computed: {
    ...mapState('global', ['origin', 'listData']),
    ...mapState('product', [
      'loading',
      'product',
      'variant',
      'sizes',
      'metafields',
    ]),
    currentVariantObject() {
      if (!this.variantsParsed) return null;
      return this.variantsParsed.find(
        (item) => this.getShadeTitle(item) === this.currentVariant,
      );
    },
    computedCategories() {
      return this.categories.filter(
        (category) => this.getFilteredVariantsByCategory(category.value).length,
      );
    },
    activeCategory() {
      if (this.isBundle) return this.localActiveCategory ?? { value: 'all' };
      return this.$store.state.product.activeCategory;
    },
    // Contains all variants reduced by swatch id
    variantsParsed() {
      return this.variantsParsedFull.reduce((arr, current) => {
        // Filter by unique swatch Id
        if (
          arr.some(
            (elm) =>
              this.getSwatchColorId(elm) === this.getSwatchColorId(current),
          )
        ) {
          return arr;
        }
        return [...arr, current];
      }, []);
    },
    variantsFiltered() {
      return this.getFilteredVariantsByCategory(this.activeCategory.value);
    },
    currentVariantDescription() {
      return this.activeShade.label;
    },
    shadesForDropdown() {
      return this.variantsParsed.map((variant) => {
        let payload = {
          ...variant,
        };

        payload = this.addCustomOverrides(payload, variant);
        const shade = this.getShadeTitle(variant);
        const colorID = this.getSwatchColorId(variant);

        return {
          label: swatchOptionMarkup({
            available: variant.available,
            swatch_color_id: colorID,
            shade,
            hexOverride: payload?.customHexColor,
            imageOverride: payload?.customSwatchImage,
          }),
          value: handleize(this.getShadeTitle(variant)),
          description: swatchDescription({
            shade,
            swatch_color_id: colorID,
          }),
        };
      });
    },
    allSizes() {
      if (!this.currentVariantObject) return [];
      const sameVariants = this.variantsParsedFull.filter(
        (variant) =>
          this.getSwatchColorId(variant) ===
          this.getSwatchColorId(this.currentVariantObject),
      );
      if (!sameVariants) return [getVariantSize(this.currentVariant)];
      return sameVariants
        .map((variant) => getVariantSize(variant))
        .reduce((arr, current) => {
          if (arr.includes(current) || current === null) {
            return arr;
          }
          return [...arr, current];
        }, []);
    },
    showFilters() {
      // Check if hide filter metafield is set on the collection or product level
      const hideFilters =
        this.hideShadeRangeFilter ||
        this.metafields?.general?.hide_shade_range_filter;
      return !hideFilters;
    },
    currentSize() {
      return getVariantSize(this.product);
    },
  },
  watch: {
    product() {
      if (!this.product.optionNames) return false;
      if (this.hasSimpleVariant) {
        this.updateVariant(this.product.variants[0]);
      }
    },
  },
  async beforeMount() {
    const handle = this.collectionHandle;
    storefront
      .request(queries.collectionWithVariants, { handle })
      .then((response) => {
        const products = response?.collection?.products?.edges;
        if (products) {
          let normalizedProducts = [];
          if (this.isGWP) {
            normalizedProducts = products?.map((product) => {
              const convertedProduct = convertGQLToProductJson(product.node);
              const filteredVariants = convertedProduct.variants.filter(
                (variant) => variant.isGWPVariant,
              );

              return { ...convertedProduct, variants: filteredVariants };
            });
            this.updateVariant(normalizedProducts[0].variants[0]);
          } else {
            for (let i = 0; i < products.length; i++) {
              const normalizedProduct = convertGQLToProductJson(
                products[i].node,
              );
              if (this.productHandles.length) {
                if (this.productHandles.includes(normalizedProduct.handle)) {
                  normalizedProducts.push(normalizedProduct);
                }
              } else {
                normalizedProducts.push(normalizedProduct);
              }
            }
          }

          this.variantsParsedFull = normalizedProducts;
          if (this.isSimplifiedTemplate) return;

          this.currentVariant = removeSizeFromTitle(this.initial);
          const initialVariantObject = this.variantsParsed.find(
            (variant) => this.getShadeTitle(variant) === this.initial,
          );

          let payload = {
            ...initialVariantObject,
          };

          payload = this.addCustomOverrides(payload, initialVariantObject);
          const shade = this.getShadeTitle(initialVariantObject);
          const colorID = this.getSwatchColorId(initialVariantObject);

          this.activeShade = {
            label: swatchOptionMarkup({
              available: initialVariantObject.available,
              swatch_color_id: colorID,
              shade,
              hexOverride: payload?.customHexColor,
              imageOverride: payload?.customSwatchImage,
            }),
            value: this.initial,
            description: swatchDescription({
              shade,
              swatch_color_id: colorID,
            }),
          };
        } else {
          console.log('Could not find collection in Storefront API', response);
        }
        return Promise.resolve(response);
      })
      .catch((error) => {
        console.error(error);
      });
  },
  methods: {
    ...mapActions('product', [
      'updateProduct',
      'updateVariant',
      'updateProductAsNewPage',
      'updateActiveImage',
      'updateActiveCategory',
    ]),
    buildShadeClassObject(variant) {
      return {
        [variant.colorClass]: !Boolean(variant.customSwatchImage),
        active: this.currentVariant === this.getShadeTitle(variant),
        unavailable: this.getShadeAvailability(variant),
        new: variant?.additionalCategories?.includes('new'),
      };
    },
    buildLabelNew(variant) {
      if (!variant?.additionalCategories?.includes('new')) return '';
      return variant?.additionalCategories?.includes('new');
    },
    buildInlineStyleObject({ customSwatchImage, customHexColor }) {
      if (!Boolean(customSwatchImage) && !Boolean(customHexColor)) return;

      let styleObject = {};
      if (Boolean(customHexColor)) {
        styleObject = {
          backgroundColor: `${customHexColor} !important`,
        };
      }

      if (Boolean(customSwatchImage)) {
        styleObject = {
          backgroundColor: 'unset !important',
          backgroundImage: `url('${customSwatchImage}')`,
        };
      }

      return styleObject;
    },
    async changeProduct(handle, variant, size = null) {
      if (handle) {
        let variantsList = this.variantsParsedFull;
        if (size !== null) {
          variantsList = variantsList.filter(
            (variant) => getVariantSize(variant) === size,
          );
        }
        const variantObject = variantsList.find(
          (item) => removeSizeFromTitle(item.title) === variant,
        );
        if (!this.isBundle) {
          if (this.isProductFeature) {
            await this.updateProduct(handle);
          } else {
            await this.updateProductAsNewPage({
              previousVariant: this.currentVariant,
              variant,
              handle,
            });
          }

          datalayer.dataLayerPush(
            'eeProductDetail',
            getSwatchChangeProductData(this.product),
          );
        }
        // Remove the size part
        const shadeName = removeSizeFromTitle(
          this.getShadeTitle(variantObject),
        );
        const updatedVariantObject = {
          ...variantObject,
          shade: shadeName,
        };
        this.currentVariant = shadeName;

        let payload = {
          ...updatedVariantObject,
        };

        payload = this.addCustomOverrides(payload, updatedVariantObject);

        const shade = this.getShadeTitle(updatedVariantObject);
        const colorID = this.getSwatchColorId(updatedVariantObject);

        this.activeShade = {
          label: swatchOptionMarkup({
            available: updatedVariantObject.available,
            swatch_color_id: colorID,
            shade,
            hexOverride: payload?.customHexColor,
            imageOverride: payload?.customSwatchImage,
          }),
          value: this.getShadeHandle(updatedVariantObject),
          description: swatchDescription({
            shade,
            swatch_color_id: colorID,
          }),
        };
        if (this.isBundle) {
          this.$emit('change-product', updatedVariantObject);
        }
        bus.$emit('update-variant', this.variant);
        bus.$emit('update-product', updatedVariantObject);

        await this.trackProductChange({
          product: updatedVariantObject,
          type: 'shade',
          variant: this.variant,
        });
      }
    },
    async trackProductChange(data) {
      const { product } = data;

      const otherData = {
        variant: product.variants[0].id,
      };

      trackItemView(product, otherData, 'View All Details');
    },
    handle(string) {
      return handleize(string);
    },
    onCategoryChange(category) {
      if (this.isBundle) this.localActiveCategory = category;
      else this.updateActiveCategory(category);
    },
    onSwatchChange(handle, variant) {
      this.changeProduct(handle, variant);
      const variantChangeEvent = new CustomEvent('variant:change', {
        detail: {
          variant,
          handle,
        },
      });

      window.dispatchEvent(variantChangeEvent);
    },
    onShadeChange(shade) {
      const variant = this.variantsParsed.find(
        (variant) =>
          handleize(this.getShadeTitle(variant)) ===
          handleize(shade.value).replace(/[?!]/g, ''),
      );
      if (variant) {
        this.onCategoryChange(this.categories[0]);
        this.changeProduct(variant.handle, variant.title);
      }
    },
    addCustomOverrides(payload, variant) {
      // Check for custom hex color defined in Product metafields
      if (Boolean(variant.metafields?.swatch_management?.custom_hex)) {
        const customHexColor = variant.metafields.swatch_management.custom_hex;
        payload = {
          ...payload,
          customHexColor,
        };
      }

      // Check for custom image defined in Product metafields
      if (Boolean(variant.metafields?.swatch_management?.custom_image)) {
        const imageJSON = JSON.parse(
          variant.metafields.swatch_management.custom_image,
        );
        const { src } = imageJSON[0];
        payload = {
          ...payload,
          customSwatchImage: src,
        };
      }

      // Check for custom categories defined in Product metafields
      if (
        Boolean(variant.metafields?.swatch_management?.additional_categories)
      ) {
        const additionalCategories =
          variant.metafields.swatch_management.additional_categories
            .split('|')
            .map((category) => handleize(category));

        payload = {
          ...payload,
          additionalCategories,
        };
      }
      return payload;
    },
    getFilteredVariantsByCategory(category = 'all') {
      const variantsWithColorClass = this.variantsParsed.map((variant) => {
        let payload = {
          ...variant,
          colorClass: `fenty-color-${this.getSwatchColorId(variant)}`,
        };

        payload = this.addCustomOverrides(payload, variant);

        return payload;
      });

      if (category === 'all') return variantsWithColorClass;

      return variantsWithColorClass.filter((variant) => {
        const shade = this.shades.find(
          (shade) => shade.handle === this.getSwatchColorId(variant),
        );
        if (!shade) {
          return console.warn(
            'Shade not found, ensure it is configured with correct title',
          );
        }

        if (variant.additionalCategories) {
          return (
            shade.category === category ||
            variant.additionalCategories.includes(category)
          );
        }
        return shade.category === category;
      });
    },
    async changeSimpleVariant(variant) {
      this.updateVariant(variant);
      bus.$emit('update-variant', variant);

      const variantChangeEvent = new CustomEvent('variant:change', {
        detail: {
          variant,
          handle: this.product.handle,
        },
      });

      window.dispatchEvent(variantChangeEvent);

      await this.trackProductChange({
        product: this.product,
        type: 'simpleVariant',
        variant,
      });
    },
    getShadeTitle(variant) {
      return variant.title.includes(__GLOBAL__.productDelimiter)
        ? variant.title.split(__GLOBAL__.productDelimiter)[1].trim()
        : variant.title;
    },
    getShadeHandle(variant) {
      return handleize(this.getShadeTitle(variant));
    },
    getSwatchColorId(variant) {
      const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));
      if (!colorTag) return '';
      return colorTag.split('|')[1].toLowerCase();
    },
    changeSizeVariant(size) {
      const sizeVariantObjects = this.variantsParsedFull.filter(
        (variant) => getVariantSize(variant) === size,
      );
      const sameVariantWithSize = sizeVariantObjects.find(
        (variant) =>
          this.getSwatchColorId(variant) ===
          this.getSwatchColorId(this.currentVariantObject),
      );
      if (sameVariantWithSize) {
        const { handle, title } = sameVariantWithSize;
        this.changeProduct(handle, removeSizeFromTitle(title), size);
      }

      // Blur the button
      setTimeout(() => {
        if ('activeElement' in document) {
          document.activeElement.blur();
        }
      }, 200);
    },
    getShadeAvailability(variant) {
      if (this.isBundle) {
        const bundleVariant = variant.variants.find(
          (variant) => variant.isBundleVariant,
        );
        return bundleVariant ? !bundleVariant.available : !variant.available;
      }
      return !variant.available;
    },
  },
};
